import { HpService } from 'src/app/services/hp.service';
import { Component, OnInit, inject } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ButtonSettings } from 'src/app/models/button-settings.model';
import { Item } from 'src/app/models/item.model';
import { ItemForm } from 'src/app/models/item-form.model';
import { Stakeholder } from 'src/app/models/stakeholder.model';
import { ItemService } from 'src/app/services/item.service';
import { getCustomIconOnItemCategory } from 'src/app/utils/custom-icon.util';
import { ItemCategory } from 'src/app/models/item-category';
import { categorySortValue } from 'src/app/utils/item.util';
import { StakeholderService } from 'src/app/services/stakeholder.service';
import { Router } from '@angular/router';
import { HpDataProviderService } from 'src/app/services/hp-data-provider.service';
import { Hp } from 'src/app/models/hp.model';
import { ContentCardComponent } from '../../../shared-modules/content-card/content-card.component';
import { MatLabel, MatFormField, MatInput, MatSuffix } from '@angular/material/input';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';

import { AssignItemsFormComponent } from './assign-items-form/assign-items-form.component';
import { CustomButtonComponent } from '../../../shared-modules/custom-button/custom-button.component';
import { BreadcrumbComponent, BreadcrumbItem } from '../../../shared-modules/breadcrumb/breadcrumb.component';

@Component({
    selector: 'app-assign-items',
    templateUrl: './assign-items.component.html',
    styleUrls: ['./assign-items.component.scss'],
    imports: [ContentCardComponent, ReactiveFormsModule, MatLabel, MatFormField, MatInput, MatDatepickerInput, MatDatepickerToggle, MatSuffix, MatDatepicker, AssignItemsFormComponent, CustomButtonComponent, BreadcrumbComponent]
})
export class AssignItemsComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private itemService = inject(ItemService);
  private stakeholderService = inject(StakeholderService);
  private hpService = inject(HpService);
  private hpDataProviderService = inject(HpDataProviderService);
  private router = inject(Router);


  public stakeholders: Stakeholder[] = [];
  public initiator: Stakeholder = new Stakeholder()
  public itemCategory: typeof ItemCategory = ItemCategory;
  public itemForms: ItemForm[] = [];
  public buttonSettingsConfirm: ButtonSettings = {
    fontIcon: 'checklist',
  };
  public buttonSettingsReset: ButtonSettings = {
    fontIcon: 'refresh',
  };

  public readonly breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', link: '/landing-page' },
    { label: 'Instellingen', link: '/initialisation' },
    { label: 'Items en taken definiëren' }
  ];

  private hp!: Hp;
  private static CURRENT_DEADLINE_FORM_NAME = 'currentDeadline';
  private static FUTURE_DEADLINE_FORM_NAME = 'futureDeadline';

  public deadlineForm!: FormGroup;
  private currentDeadlineFormControl! : AbstractControl;
  private futureDeadlineFormControl! : AbstractControl;
  private items!: Item[];

  ngOnInit(): void {
    const hp = this.hpDataProviderService.getHp();
    if(!hp) {
      throw new Error('Hp is undefined');
    }
    this.hp = hp;
    this.deadlineForm = new FormGroup({
      [AssignItemsComponent.CURRENT_DEADLINE_FORM_NAME]: new FormControl(this.hp.deadlineCurrentSituation, { validators: Validators.required, updateOn: 'blur' }),
      [AssignItemsComponent.FUTURE_DEADLINE_FORM_NAME]: new FormControl(this.hp.deadlineFutureSituation, { validators: Validators.required, updateOn: 'blur' })
    });
    this.currentDeadlineFormControl = this.deadlineForm.get(AssignItemsComponent.CURRENT_DEADLINE_FORM_NAME)!;
    this.futureDeadlineFormControl = this.deadlineForm.get(AssignItemsComponent.FUTURE_DEADLINE_FORM_NAME)!;
    this.stakeholderService.getStakeholders().subscribe(stakeholders => {
      this.stakeholders = stakeholders
    });

    this.hpService.getInitiator().subscribe(initiator => {
      this.initiator = initiator
      this.initItemForms();
    });

    this.initDeadlineForms();
  }

  public onItemFormUpdate(updatedItemForm: ItemForm): void {
    this.itemForms.find(itemForm => itemForm.itemCategory === updatedItemForm.itemCategory)!.formGroup = updatedItemForm.formGroup;
  }

  public onSubmit(): void {
    if (this.isAllFormValid()) {
      this.itemService.updateItems(this.itemFormsToItems()).subscribe({
        next: () => this.router.navigate(['/initialisation']),
        error: (e: Error) => console.error(e)
      });
    } else {
      this.markAllAsTouched();
    }
  }

  public reset() {
    this.router.navigate(['/initialisation'])
  }

  get currentDeadlineFormControlName() {
    return AssignItemsComponent.CURRENT_DEADLINE_FORM_NAME;
  }

  get futureDeadlineFormControlName() {
    return AssignItemsComponent.FUTURE_DEADLINE_FORM_NAME;
  }

  private initDeadlineForms(): void {
    this.setValueChangeListners();
  }

  private setValueChangeListners(): void {
    this.currentDeadlineFormControl.valueChanges.subscribe((input: string) => {
      this.hp.deadlineCurrentSituation = new Date(input);
      this.updateDeadline();
    });

    this.futureDeadlineFormControl.valueChanges.subscribe((input: string) => {
      this.hp.deadlineFutureSituation = new Date(input);
      this.updateDeadline();
    });
  }

  private updateDeadline(): void {
    this.hpService.updateHp(this.hp).subscribe({
      next: () => this.hpDataProviderService.updateLocalHp(this.hp),
      error: (e: Error) => console.error(e)
    });
  }

  private initItemForms(): void {
    this.itemService.getItemsWithContentAsString(true, true).subscribe(items => {
      this.items = items;
      this.fillItemFormsPerCategory(items);
      this.itemForms.sort((first, next) => categorySortValue(first.itemCategory) - categorySortValue(next.itemCategory));
    });
  }

  private fillItemFormsPerCategory(items: Item[]): void {
    Object.values(ItemCategory).forEach(itemCategory => {
      const itemFormArray: FormArray = this.formBuilder.array([]);
      const categoryItems: Item[] = items.filter(item => item.type.category === itemCategory);
      categoryItems.forEach(item => itemFormArray.push(this.getItemFormGroup(item.type.name, item.stakeholder, item.enabled, item.custom)));
      this.itemForms.push(this.createItemForm(itemFormArray, itemCategory));
    });
  }

  private createItemForm(itemFormArray: FormArray, itemCategory: ItemCategory): ItemForm {
    return {
      itemCategory: itemCategory,
      customIcon: getCustomIconOnItemCategory(itemCategory),
      formGroup: this.formBuilder.group({
        items: itemFormArray
      })
    }
  }

  private getItemFormGroup(name: string, stakeholder: Stakeholder, active = true, custom = false): FormGroup {
    const itemGroup: FormGroup = this.formBuilder.group({});
    const defaultItem = {
      name,
      stakeholder,
      active,
      custom
    }

    Object.entries(defaultItem).forEach(([key, value]) => {
      const defaultValue = key === 'name' || key === 'active' || key === 'custom' ? value : (value as Stakeholder).userId;
      itemGroup.addControl(
        key,
        this.formBuilder.nonNullable.control(defaultValue, { validators: [Validators.required], updateOn: 'blur' })
      );
    });
    return itemGroup;
  }

  private isAllFormValid(): boolean {
    return this.itemForms.findIndex(itemForm => itemForm.formGroup.invalid) === -1;
  }

  private markAllAsTouched(): void {
    this.itemForms.forEach(itemForm => {
      itemForm.formGroup.markAllAsTouched();
    });
  }

  private itemFormsToItems(): Item[] {
    const items: Item[] = [];

    this.itemForms.forEach(itemForm => {
      const formControls: FormControl[] = (itemForm.formGroup.get('items') as FormArray).controls as FormControl[];
      formControls.forEach(control => {
        const item = this.items.find(item => item.type.name === control.get('name')?.value);
        items.push(this.createNewItem(item, control, itemForm));


    });
  });
  return items;
}

  private createNewItem(item: Item | undefined, control: FormControl, itemForm: ItemForm): Item {
    if (item) {
      return {
        itemId: item.itemId,
        hpId: item.hpId,
        type: {
          name: control.get('name')?.value,
          category: itemForm.itemCategory,
          icon: ''
        },
        custom: item.custom,
        stakeholder: this.getStakeholder(control.get('stakeholder')?.value),
        enabled: control.get('active')?.value,

      }
    }
    else {
      return {
        itemId: -1,
        hpId: this.hp.hpId,
        type: {
          name: control.get('name')?.value,
          category: itemForm.itemCategory,
          icon: ''
        },
        custom: true,
        stakeholder: this.getStakeholder(control.get('stakeholder')?.value),
        enabled: control.get('active')?.value
      }
    }

  }

  private getStakeholder(stakeholderId: number): Stakeholder {
    return {
      userId: stakeholderId,
      name: '',
      organisation: '',
      email: ''
    };
  }
}
